/**
 * Voice REST adapter. Every path matches `apps/api/src/routes/voice.ts`.
 *
 * There is no mock fixture set: a voice room is only meaningful against a live
 * SFU, and a fake occupancy list would be a lie the sidebar renders as truth.
 * Under `VITE_USE_MOCK` the directory is EMPTY and joining reports plainly that
 * voice needs the real backend.
 */
import type { ChannelId, TeamId } from '@cyphex/types/common';
import type { Channel } from '@cyphex/types/teams';
import type {
  JoinVoiceResponse,
  VoiceChannelSummary,
  VoiceOccupant,
  VoiceStateRequest,
} from '@cyphex/types/voice';
import { apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api/client';
import { USE_MOCK } from '@/lib/api/env';

export interface JoinVoiceBody {
  readonly lowBandwidth?: boolean;
  readonly isMobile?: boolean;
  readonly isMuted?: boolean;
}

const NO_VOICE_IN_MOCK = 'Voice rooms need the live backend (VITE_USE_MOCK is on).';

export const voiceApi = {
  directory: (teamId: TeamId | null, signal?: AbortSignal): Promise<VoiceChannelSummary[]> => {
    if (USE_MOCK) return Promise.resolve([]);
    const query = teamId === null ? '' : `?teamId=${teamId}`;
    return apiGet<{ channels: VoiceChannelSummary[] }>(`/voice/channels${query}`, signal).then(
      (payload) => payload.channels,
    );
  },

  occupants: (channelId: ChannelId, signal?: AbortSignal): Promise<VoiceOccupant[]> => {
    if (USE_MOCK) return Promise.resolve([]);
    return apiGet<{ occupants: VoiceOccupant[] }>(
      `/voice/channels/${channelId}/occupants`,
      signal,
    ).then((payload) => payload.occupants);
  },

  join: (channelId: ChannelId, body: JoinVoiceBody = {}): Promise<JoinVoiceResponse> => {
    if (USE_MOCK) return Promise.reject(new Error(NO_VOICE_IN_MOCK));
    return apiPost<JoinVoiceResponse>(`/voice/channels/${channelId}/join`, body);
  },

  leave: (channelId: ChannelId): Promise<void> => {
    if (USE_MOCK) return Promise.resolve();
    return apiPost<{ ok: true }>(`/voice/channels/${channelId}/leave`, {}).then(() => undefined);
  },

  /**
   * Mirror local media state AND keep the session alive. The server reaps
   * sessions that stop reporting, so this is the client's heartbeat too.
   */
  setState: (channelId: ChannelId, patch: VoiceStateRequest): Promise<VoiceOccupant[]> => {
    if (USE_MOCK) return Promise.resolve([]);
    return apiPatch<{ occupants: VoiceOccupant[] }>(
      `/voice/channels/${channelId}/state`,
      patch,
    ).then((payload) => payload.occupants);
  },

  create: (body: {
    teamId: TeamId;
    name: string;
    description?: string;
    capacity?: number;
  }): Promise<Channel> => {
    if (USE_MOCK) return Promise.reject(new Error(NO_VOICE_IN_MOCK));
    return apiPost<Channel>('/voice/channels', body);
  },

  remove: (channelId: ChannelId): Promise<void> => {
    if (USE_MOCK) return Promise.resolve();
    return apiDelete<{ ok: true }>(`/channels/${channelId}`).then(() => undefined);
  },
} as const;
